import React, { useState } from 'react';
import './StupidSystemDesigns.css';

const atmCode = String.raw`
#include <iostream>
#include <vector>
#include <string>
#include <map>
#include <unordered_map>

using namespace std;

class Account {
    int cardNumber;
    string userName;
    int pin;
    int balance;

public:
    Account(int cardNum, string name, int pin, int initialBalance = 0) {
        cardNumber = cardNum;
        userName = name;
        this->pin = pin;
        balance = initialBalance;
    }

    int getBalance() { return balance; }
    void addBalance(int amt) { balance += amt; }
    void subBalance(int amt) { balance -= amt; }
    bool checkPin(int enteredPin) { return pin == enteredPin; }
    int getCardNumber() { return cardNumber; }
    string getUserName() { return userName; }
};

class CashDispenser {
    map<int, int, greater<int>> cashMap;

public:
    void addNotes(int denomination, int count) {
        cashMap[denomination] += count;
    }

    bool dispense(int amount, vector<pair<int, int>>& dispensedNotes) {
        auto tempMap = cashMap;

        for (auto& pair : tempMap) {
            if (amount <= 0) break;

            int denom = pair.first;
            int availableCount = pair.second;
            int needed = amount / denom;
            int take = min(needed, availableCount);

            if (take > 0) {
                dispensedNotes.push_back({denom, take});
                amount -= (take * denom);
                pair.second -= take;
            }
        }

        if (amount == 0) {
            cashMap = tempMap;
            return true;
        }

        dispensedNotes.clear();
        return false;
    }
};

class ATM {
    enum State { IDLE, IN_USE };

    unordered_map<int, Account*> cardMap;
    unordered_map<string, Account*> userMap;

    int cardNumberInUse = -1;
    State ATMState;
    CashDispenser dispenser;

public:
    ATM() {
        ATMState = IDLE;
        cardNumberInUse = -1;
    }

    void addBankDatabaseRecord(Account* acc) {
        cardMap[acc->getCardNumber()] = acc;
        userMap[acc->getUserName()] = acc;
    }

    void stockATM(int denom, int count) {
        dispenser.addNotes(denom, count);
    }

    void login(int cardNum, int pin) {
        if (!cardMap.count(cardNum)) {
            cout << " [LOGIN FAILED] Invalid card entered\n";
            return;
        }

        Account* acct = cardMap[cardNum];
        if (!acct->checkPin(pin)) {
            cout << " [LOGIN FAILED] Incorrect pin entered\n";
            return;
        }

        ATMState = IN_USE;
        cardNumberInUse = cardNum;
        cout << " [LOGIN SUCCESS] Welcome, " << acct->getUserName() << "!\n";
    }

    void checkBal() {
        if (ATMState != IN_USE) {
            cout << " [CHECK BALANCE FAILED] Please login first\n";
            return;
        }

        Account* acct = cardMap[cardNumberInUse];
        cout << " [BALANCE] Current balance is: $" << acct->getBalance() << "\n";
    }

    void withdraw(int amount) {
        if (ATMState != IN_USE) {
            cout << " [WITHDRAW FAILED] Please login first\n";
            return;
        }

        Account* acct = cardMap[cardNumberInUse];
        if (acct->getBalance() < amount) {
            cout << " [WITHDRAW FAILED] Insufficient bank balance\n";
            return;
        }

        vector<pair<int, int>> dispensedNotes;
        if (dispenser.dispense(amount, dispensedNotes)) {
            acct->subBalance(amount);
            cout << " [WITHDRAW SUCCESS] Dispensed $" << amount << ". Notes: ";
            for (auto& p : dispensedNotes) {
                cout << p.second << "x$" << p.first << " ";
            }
            cout << "\n";
        } else {
            cout << " [WITHDRAW FAILED] ATM cannot dispense this exact amount\n";
        }
    }

    void deposit(vector<pair<int, int>> denominationArray) {
        if (ATMState != IN_USE) {
            cout << " [DEPOSIT FAILED] Please login first\n";
            return;
        }

        int totalDeposit = 0;
        for (auto& p : denominationArray) {
            dispenser.addNotes(p.first, p.second);
            totalDeposit += (p.first * p.second);
        }

        Account* acct = cardMap[cardNumberInUse];
        acct->addBalance(totalDeposit);
        cout << " [DEPOSIT SUCCESS] Deposited $" << totalDeposit << " successfully.\n";
    }

    void transfer(int amt, string toUser) {
        if (ATMState != IN_USE) {
            cout << " [TRANSFER FAILED] Please login first\n";
            return;
        }
        if (!userMap.count(toUser)) {
            cout << " [TRANSFER FAILED] Invalid recipient username entered\n";
            return;
        }

        Account* currentAccount = cardMap[cardNumberInUse];
        if (currentAccount->getBalance() < amt) {
            cout << " [TRANSFER FAILED] Not enough money balance in account\n";
            return;
        }

        Account* toAccount = userMap[toUser];
        currentAccount->subBalance(amt);
        toAccount->addBalance(amt);
        cout << " [TRANSFER SUCCESS] $" << amt << " transferred to " << toUser << "\n";
    }

    void logout() {
        if (ATMState == IN_USE) {
            cout << " [LOGOUT] Card ejected. Goodbye!\n";
            ATMState = IDLE;
            cardNumberInUse = -1;
        }
    }
};

int main() {
    cout << "--- Initializing ATM System ---\n";
    ATM atm;

    Account* alice = new Account(1111, "Alice", 1234, 1000);
    Account* bob = new Account(2222, "Bob", 5678, 500);
    atm.addBankDatabaseRecord(alice);
    atm.addBankDatabaseRecord(bob);

    atm.stockATM(100, 10);
    atm.stockATM(20, 20);

    cout << "\n--- Testing Authentication ---\n";
    atm.login(1111, 9999);
    atm.login(1111, 1234);

    cout << "\n--- Testing Transactions ---\n";
    atm.checkBal();
    atm.withdraw(240);
    atm.checkBal();

    atm.deposit({{50, 2}, {10, 5}});
    atm.checkBal();

    atm.transfer(300, "Bob");
    atm.checkBal();
    atm.logout();

    cout << "\n--- Verifying Bob's Account ---\n";
    atm.login(2222, 5678);
    atm.checkBal();
    atm.logout();

    delete alice;
    delete bob;
    return 0;
}
`;

const stupidAtmCode = String.raw`
#include <iostream>
#include <vector>
#include <string>
#include <map>
#include <unordered_map>
#include <thread>
#include <chrono>
#include <cstdlib>
#include <ctime>

using namespace std;

class Account {
    int cardNumber;
    string userName;
    int pin;
    int balance;

public:
    Account(int cardNum, string name, int pin, int initialBalance = 0) {
        cardNumber = cardNum;
        userName = name;
        this->pin = pin;
        balance = initialBalance;
    }
    int getBalance() { return balance; }
    void addBalance(int amt) { balance += amt; }
    void subBalance(int amt) { balance -= amt; }
    bool checkPin(int enteredPin) { return pin == enteredPin; }
    int getCardNumber() { return cardNumber; }
    string getUserName() { return userName; }
};

class CashDispenser {
    map<int, int> cashMap;
public:
    void addNotes(int denomination, int count) {
        cashMap[denomination] += count;
    }

    bool dispense(int amount, vector<pair<int, int>>& dispensedNotes) {
        auto tempMap = cashMap;

        for (auto& pair : tempMap) {
            if (amount <= 0) break;

            int denom = pair.first;
            int availableCount = pair.second;
            int needed = amount / denom;
            int take = min(needed, availableCount);

            if (take > 0) {
                dispensedNotes.push_back({denom, take});
                amount -= (take * denom);
                pair.second -= take;
            }
        }

        if (amount == 0) {
            cashMap = tempMap;
            return true;
        }

        dispensedNotes.clear();
        return false;
    }
};

class ATM {
    enum State { IDLE, IN_USE };

    unordered_map<int, Account*> cardMap;
    unordered_map<string, Account*> userMap;

    int cardNumberInUse = -1;
    State ATMState;
    CashDispenser dispenser;

    bool isAnnoyingTimeout() {
        cout << " [SYSTEM] Processing... (Please do not breathe too heavily)...\n";
        this_thread::sleep_for(chrono::milliseconds(1500));

        if (rand() % 5 == 0) {
            cout << " [TIMEOUT ERROR] You took 0.01 seconds too long. Session terminated.\n";
            logout();
            return true;
        }
        return false;
    }

public:
    ATM() {
        ATMState = IDLE;
        cardNumberInUse = -1;
        srand(time(0));
    }

    void addBankDatabaseRecord(Account* acc) {
        cardMap[acc->getCardNumber()] = acc;
        userMap[acc->getUserName()] = acc;
    }

    void stockATM(int denom, int count) {
        dispenser.addNotes(denom, count);
    }

    void login(int cardNum, int pin) {
        if (isAnnoyingTimeout()) return;

        if (!cardMap.count(cardNum) || !cardMap[cardNum]->checkPin(pin)) {
            cout << " [LOGIN FAILED] Card rejected or PIN incorrect. We won't tell you which.\n";
            return;
        }
        ATMState = IN_USE;
        cardNumberInUse = cardNum;
        cout << " [LOGIN SUCCESS] Welcome, " << cardMap[cardNum]->getUserName() << ".\n";
    }

    void checkBal() {
        if (ATMState != IN_USE) { cout << " [ERROR] Who are you?\n"; return; }
        if (isAnnoyingTimeout()) return;

        Account *acct = cardMap[cardNumberInUse];

        cout << " [FEE ALERT] Deducting $2.00 Screen Illumination Fee.\n";
        acct->subBalance(2);

        if (acct->getBalance() < 0) {
            cout << " [OVERDRAFT ALERT] Balance is negative! Applying $35 Overdraft Fee.\n";
            acct->subBalance(35);
        }

        cout << " [BALANCE] Current balance is: $" << acct->getBalance() << "\n";
    }

    void withdraw(int amount) {
        if (ATMState != IN_USE) { cout << " [ERROR] Login first.\n"; return; }
        if (isAnnoyingTimeout()) return;

        Account *acct = cardMap[cardNumberInUse];
        if (acct->getBalance() < amount) {
            cout << " [WITHDRAW FAILED] You are too poor for this transaction.\n";
            return;
        }

        vector<pair<int, int>> dispensedNotes;
        if (dispenser.dispense(amount, dispensedNotes)) {
            acct->subBalance(amount);
            cout << " [WITHDRAW SUCCESS] Dispensed $" << amount << ".\n [RECEIPT] Please take your massive stack of notes:\n  -> ";
            for (auto& p : dispensedNotes) {
                cout << p.second << "x$" << p.first << "  ";
            }
            cout << "\n [SYSTEM] Good luck fitting that in your wallet.\n";
        } else {
            cout << " [WITHDRAW FAILED] ATM is out of annoying denominations.\n";
        }
    }

    void deposit(vector<pair<int, int>> denominationArray) {
        if (ATMState != IN_USE) { cout << " [ERROR] Login first.\n"; return; }
        if (isAnnoyingTimeout()) return;

        int totalDeposit = 0;
        for (auto& p : denominationArray) {
            int denom = p.first;
            int count = p.second;

            for(int i = 0; i < count; i++) {
                if (rand() % 10 == 0) {
                    cout << " [CRUNCH] ...Oops. The slot jammed and destroyed a $" << denom << " bill. Our apologies.\n";
                } else {
                    dispenser.addNotes(denom, 1);
                    totalDeposit += denom;
                }
            }
        }

        Account *acct = cardMap[cardNumberInUse];
        acct->addBalance(totalDeposit);
        cout << " [DEPOSIT SUCCESS] Actually credited: $" << totalDeposit << "\n";
    }

    void logout() {
        if (ATMState == IN_USE) {
            cout << " [LOGOUT] Card aggressively ejected onto the floor. Goodbye!\n";
            ATMState = IDLE;
            cardNumberInUse = -1;
        }
    }
};

int main() {
    cout << "--- Booting Windows 95 ATM Subsystem ---\n";
    ATM atm;

    Account* alice = new Account(1111, "Alice", 1234, 1000);
    atm.addBankDatabaseRecord(alice);

    atm.stockATM(100, 10);
    atm.stockATM(20, 10);
    atm.stockATM(5, 50);
    atm.stockATM(1, 300);

    cout << "\n--- User Attempts to Use ATM ---\n";
    atm.login(1111, 1234);
    atm.checkBal();

    cout << "\n[Alice requests $240...]\n";
    atm.withdraw(240);

    cout << "\n[Alice tries to deposit 5x$20 bills...]\n";
    atm.deposit({{20, 5}});

    atm.checkBal();
    atm.logout();

    delete alice;
    return 0;
}
`;

function ATMDesign({ theme, onBackClick }) {
    const [copied, setCopied] = useState({
        good: false,
        stupid: false,
    });

    const handleCopyCode = async (codeKey, codeText) => {
        try {
            await navigator.clipboard.writeText(codeText);
            setCopied((prev) => ({ ...prev, [codeKey]: true }));
            window.setTimeout(() => {
                setCopied((prev) => ({ ...prev, [codeKey]: false }));
            }, 1800);
        } catch (error) {
            console.error('Copy failed:', error);
            setCopied((prev) => ({ ...prev, [codeKey]: false }));
        }
    };

    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>ATM Design</h1>
                            <p className="system-designs-subtitle">
                                A banking terminal that verifies identity, manages account state, and dispenses cash without accidentally becoming a magic vending machine.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>
                            Back to Designs
                        </button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Functional Requirements</h2>
                            <ul>
                                <li>Authenticate the user using a card number and PIN before allowing access.</li>
                                <li>Allow checking the current account balance.</li>
                                <li>Support cash withdrawal while ensuring sufficient funds and available notes.</li>
                                <li>Support cash deposit and update the account balance immediately.</li>
                                <li>Allow transferring money to another valid user account.</li>
                                <li>Maintain a secure session so operations only happen after login.</li>
                                <li>Handle invalid card, invalid PIN, insufficient funds, and unavailable cash gracefully.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Design</h2>
                            <ul>
                                <li>The ATM owns the login/session state and validates whether a user is currently active.</li>
                                <li>The Account entity stores identity, PIN, and balance data for a specific banking member.</li>
                                <li>The CashDispenser handles note inventory and exact-denomination dispensing logic.</li>
                                <li>Withdrawal checks both bank balance and ATM cash availability before completing.</li>
                                <li>Deposit increments the account balance and adds notes to the machine inventory.</li>
                                <li>Transfer validates the recipient and updates both balances atomically in the current flow.</li>
                                <li>A small in-memory map simulates the bank database and user lookups for demonstration purposes.</li>
                            </ul>
                        </div>

                        <div className="design-section stupid-design-section">
                            <h2>Stupid Design</h2>
                            <ul>
                                <li><strong>Penny-Pincher Algorithm:</strong> The cash dispenser uses a standard map, which iterates from smallest to largest denominations, so a $500 withdrawal may drain every $1 and $5 bill before touching a $100 note.</li>
                                <li><strong>Aggressive Timeout Roulette:</strong> Every action has a random 20% chance of timing out because the user allegedly blinked too slowly, immediately eating the card and forcing a logout.</li>
                                <li><strong>90s Dial-up Simulator:</strong> Every button press includes a brief sleep delay, making every transaction feel like it is being processed by a dial-up modem from 1998.</li>
                                <li><strong>Predatory Viewing Fees:</strong> Checking a balance costs a $2 convenience fee, and if that drops the balance below zero, an additional $35 overdraft fee appears like a second trapdoor.</li>
                                <li><strong>The “Sticky Slot” Deposit:</strong> Deposits have a 10% chance to eat the bill and never credit it to the account, which is fantastic for both suspense and the ATM’s self-esteem.</li>
                                <li><b>NOTE:</b> I don’t know if the myth that ATMs make “cash counting” noises is a speaker trick or the actual sound of the machine; either way, customers are equally convinced the money is being counted correctly.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Main Entities</h2>
                            <ul>
                                <li>ATM: handles authentication, session, and transaction orchestration.</li>
                                <li>Account: represents a cardholder, PIN, and bank balance.</li>
                                <li>CashDispenser: owns denominations and dispensing logic for withdrawals and deposits.</li>
                                <li>Bank database (simulated backend DB):map card number {'->'} account and username {'->'} account.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>ATM sample code:</h2>
                                <button type="button" className="copy-code-btn" onClick={() => handleCopyCode('good', atmCode)}>
                                    {copied.good ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{atmCode}</code></pre>
                        </div>

                        <div className="design-section stupid-design-section">
                            <div className="design-code-header">
                                <h2>Stupid ATM sample code:</h2>
                                <button type="button" className="copy-code-btn" onClick={() => handleCopyCode('stupid', stupidAtmCode)}>
                                    {copied.stupid ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{stupidAtmCode}</code></pre>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        This is the sort of thing people call “simple” until the logout silently failed and the next user pulls money from your account!
                    </div>

                    <button className="back-btn" onClick={onBackClick}>
                        Back to Designs
                    </button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default ATMDesign;
